import { IMediaStats, IChunkStats } from 'src/interfaces/Stats';
import { formatBandwidth, formatBytesAsKb, formatSeconds } from 'src/utils/formatUtils';
import { statsInitialState } from 'src/store/reducers/stats';
import { MEDIA_TYPE } from 'src/interfaces/enums';
import projectPackage from '../../../package.json';
import { detectConnectionType, getBrowserInfo } from '../../utils/browserUtils';
import ApplicationService from '../ApplicationService';

class StatsService {
  get playbackQuality() {
    const playbackQuality = ApplicationService.player?.getVideoPlaybackQuality;
    const { droppedVideoFrames, totalVideoFrames } = playbackQuality;

    return { droppedVideoFrames, totalVideoFrames };
  }

  get mediaResolution() {
    if (ApplicationService.player?.vhs) {
      const media = ApplicationService.player?.vhs.playlists.media();
      const { RESOLUTION } = media.attributes;

      if (RESOLUTION) {
        const mediaResolution = `${RESOLUTION.width}x${RESOLUTION.height}`;
        return mediaResolution;
      }
    }

    return `${ApplicationService.player?.tech?.videoWidth()}x${ApplicationService.player?.tech?.videoHeight()}`;
  }

  getMediaData() {
    const data: IMediaStats = statsInitialState.stats.media;
    const applicationData = ApplicationService.applicationData;
    const vhs = ApplicationService.player?.vhs;

    if (!applicationData) return {};

    if (vhs) {
      const media = vhs.playlists.media();
      const { BANDWIDTH, CODECS } = media.attributes;

      data.bandwidth = formatBandwidth(BANDWIDTH);
      data.codecs = CODECS;
    } else {
      data.codecs = '-';
      data.bandwidth = 0;
    }

    data.mediaCode = applicationData.mediaCode;
    data.user = applicationData.userCode ?? '';
    data.currentDate = `${new Date().toUTCString()}`;
    data.mediaResolution = this.mediaResolution;
    data.deviceConnection = detectConnectionType();

    return {
      ...data,
      ...this.playbackQuality,
    };
  }

  getGeneralData() {
    const vhs = ApplicationService.player?.vhs;

    const data = {
      hls: Boolean(vhs),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      projectVersion: projectPackage.version,
      experimentName: ApplicationService.applicationData?.experimentName,
      duration: formatSeconds(ApplicationService.player?.duration || 0),
      volume: ((ApplicationService.player?.volumePercent || 0) / 100).toFixed(2),
      measuredBandwidth: vhs ? formatBandwidth(vhs.bandwidth) : 0,
      browserInfo: getBrowserInfo(),
      viewId: ApplicationService.player?.viewId,
    };

    return data;
  }

  getBufferedData() {
    const buffered = ApplicationService.player?.buffered;

    const currentTime = formatSeconds(ApplicationService.player?.currentTime || 0);
    const bufferEnd = buffered?.end(buffered.length - 1) || 0;
    const bufferStart = buffered?.start(0);
    const timeRemaining = bufferEnd - (ApplicationService.player?.currentTime || 0);
    const bufferTimeRemaining = formatSeconds(timeRemaining);

    return { bufferEnd, bufferStart, bufferTimeRemaining, currentTime };
  }

  getChunkData() {
    const textTracks = ApplicationService.player?.textTracks;
    const track = textTracks && textTracks[0];

    const data: IChunkStats = statsInitialState.stats.chunks;

    if (track && track.activeCues) {
      const activeCue = track.activeCues[0];

      if (activeCue && ApplicationService.applicationData.mediaType === MEDIA_TYPE.video) {
        const { bandwidth, byteLength, uri } = activeCue.value;

        data.chunkBitrate = formatBandwidth(bandwidth);
        data.chunkSize = formatBytesAsKb(byteLength);
        data.chunkURI = uri;
      }
    }

    return data;
  }
}

export default new StatsService();
